<template>
   <report
        :report="report"
        :versions="versions"
        :loading="loading"
        :refreshing="refreshing"
        @reload="reload"
        @load-version="load_version"
    >
        <div class="lam-graph-container">
            <div class="uk-grid uk-grid-small uk-grid-width-1-2">
                <div>
                    <div class="uk-flex">
                        <div class="uk-margin-right">
                            <ul class="uk-subnav uk-subnav-line">
                                <li class="uk-disabled"><a href="">Staff</a></li>
                                <li :class="{'uk-active': !state.filter}"><a href @click.prevent="set_state_value('filter', '')">All</a></li>
                                <li
                                    v-for="filterable_role in filterable_roles"
                                    :key="filterable_role.id"
                                    :class="{'uk-active': state.filter === filterable_role.id}"
                                >
                                    <a href @click.prevent="set_state_value('filter', filterable_role.id)">
                                        {{ filterable_role.name }}
                                    </a>
                                </li>
                            </ul>
                        </div>
                        <div>
                            <ul class="uk-subnav uk-subnav-line">
                                <li class="uk-disabled"><a href="">Studio</a></li>
                                <li :class="{'uk-active': !state.studio_id}"><a href @click.prevent="set_state_value('studio_id', '')">All</a></li>
                                <li
                                    v-for="studio in studios"
                                    :key="studio.id"
                                    :class="{'uk-active': state.studio_id === studio.id}"
                                >
                                    <a href @click.prevent="set_state_value('studio_id', studio.id)">{{ studio.name }}</a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div class="uk-text-right">
                    <div class="uk-button-group lam-graph-filter">
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.scope === 'near-future'}"
                            @click.prevent="set_scope('near-future')"
                        >
                            This Week
                        </base-button>
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.scope === 'next-week'}"
                            @click.prevent="set_scope('next-week')"
                        >
                            Next Week
                        </base-button>
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.scope === 'two-weeks-out'}"
                            @click.prevent="set_scope('two-weeks-out')"
                        >
                            2 Weeks Out
                        </base-button>
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.scope === 'three-weeks-out'}"
                            @click.prevent="set_scope('three-weeks-out')"
                        >
                            3 Weeks Out
                        </base-button>
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.scope === 'four-weeks-out'}"
                            @click.prevent="set_scope('four-weeks-out')"
                        >
                            4 Weeks Out
                        </base-button>
                    </div>
                    <br />
                    <div class="uk-button-group lam-graph-filter">
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.scope === 'last-week'}"
                            @click.prevent="set_scope('last-week')"
                        >
                            Last Week
                        </base-button>
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.scope === 'two-weeks-ago'}"
                            @click.prevent="set_scope('two-weeks-ago')"
                        >
                            2 Weeks Ago
                        </base-button>
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.scope === 'three-weeks-ago'}"
                            @click.prevent="set_scope('three-weeks-ago')"
                        >
                            3 Weeks Ago
                        </base-button>
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.scope === 'four-weeks-ago'}"
                            @click.prevent="set_scope('four-weeks-ago')"
                        >
                            4 Weeks Ago
                        </base-button>
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.scope === 'five-weeks-ago'}"
                            @click.prevent="set_scope('five-weeks-ago')"
                        >
                            5 Weeks Ago
                        </base-button>
                    </div>
                </div>
            </div>
            <div class="uk-grid uk-grid-small uk-margin-bottom lam-graph">
                <div class="uk-width-1-1" style="position: relative;">
                    <milestones-graph
                        ref="chart"
                        :chart-data="chart_data"
                        :y-max="ymax"
                        style="height: 200px;"
                        @click="route_to_record"
                    />
                </div>
            </div>
        </div>

        <data-table
            :id="state_identifier"
            :columns="visible_columns"
            :rows="rows"
            :total="state.total"
            :found="state.found"
            :current-page="state.page"
            :total-pages="nb_pages"
            :searchable="false"
            :can-add="false"
            table-class="lam-subrecords-list"
            empty-title="No deliverables"
            @sort="sort_by"
            @search="search"
            @page="goto_page"
        >
            <template
                v-slot:summary="{ row }"
            >
                <h6>{{ row.name }}</h6>
            </template>
            <template
                v-slot:tbody="{ row, field }"
            >
                <router-link
                    v-if="field === 'sortable_number'"
                    :to="{ name: 'project.edit', params: { id: row.project_id } }"
                >
                    {{ project_for_row(row).sortable_number | lam.project-number }} {{ project_for_row(row).name }}
                </router-link>
                <span v-if="field === 'ends_at_as_string'">{{ ends_at(row) | nibnut.date("dddd MM/DD") }}</span>
                <span v-else-if="field === 'title'">
                    {{ row.title }}
                    <div
                        v-if="row.milestone_title"
                        class="uk-text-small uk-text-muted"
                    >
                        {{ row.milestone_title }}
                    </div>
                </span>
                <span v-else-if="field.match(/^(studio_list|team_list)/)">{{ (project_for_row(row)[field] || []).join(", ") }}</span>
                <span v-else>{{ row[field] }}</span>
            </template>
        </data-table>
    </report>
</template>

<script type="text/javascript">
import is_report from "./IsReport"
import handles_projects from "./HandlesProjects"
import is_data_table_source from "../../mixins/IsDataTableSource"
import uses_color_palette from "../../mixins/UsesColorPalette"

import Report from "./Report.vue"
import BaseButton from "../../ui/BaseButton"
import MilestonesGraph from "./MilestonesGraph.js"

export default {
    mixins: [is_report, handles_projects, is_data_table_source, uses_color_palette],
    components: {
        Report,
        BaseButton,
        MilestonesGraph
    },
    methods: {
        post_load (response) {
            this.versions = []
            if(response && response.meta && response.meta.versions) this.versions = response.meta.versions

            this.task_ids = []
            if(response && response.meta && response.meta.updates && response.meta.updates.task) this.task_ids = response.meta.updates.task.map(task => task.id)
        },
        project_for_row (row) {
            if(!row) return {}
            return this.entity_records("project").find(project => project.id === row.project_id) || {}
        },
        ends_at (task) {
            return task.proxied_ends_at_as_string || task.ends_at_as_string
        },
        set_scope (scope) {
            const was_forward = !!this.state.scope.match(/(near|next|out)/)
            const is_forward = !!scope.match(/(near|next|out)/)
            this.set_state_value("scope", scope)
            if(was_forward !== is_forward) this.reload(false)
        }
    },
    computed: {
        state_identifier () {
            return this.report_id
        },
        fields () {
            return ["ns::project;fieldset::default,team_list,studio_list,hex_color,sortable_number", "ns::task;ends_at,proxied_ends_at_as_string,title,project_id,milestone_id,milestone_title,phase_name"]
        },
        chart_data () {
            const data = { labels: [], datasets: [] }
            const range_start = this.$moment().startOf("isoWeek")
            switch (this.state.scope) {
            case "next-week":
                range_start.add(1, "weeks")
                break
            case "two-weeks-out":
                range_start.add(2, "weeks")
                break
            case "three-weeks-out":
                range_start.add(3, "weeks")
                break
            case "four-weeks-out":
                range_start.add(4, "weeks")
                break
            case "last-week":
                range_start.subtract(1, "weeks")
                break
            case "two-weeks-ago":
                range_start.subtract(2, "weeks")
                break
            case "three-weeks-ago":
                range_start.subtract(3, "weeks")
                break
            case "four-weeks-ago":
                range_start.subtract(4, "weeks")
                break
            case "five-weeks-ago":
                range_start.subtract(5, "weeks")
                break
            }

            if(this.report) {
                const project_datasets = {}
                const report_dataset = this.report.dataset[range_start.format("YYYY-MM-DD")] || []

                const default_dataset = []
                Object.keys(report_dataset).forEach(initials => {
                    const user = this.raw_user_records.find(user => user.initials === initials)
                    if(user && (!this.state.filter || (this.state.filter === user.role)) && (!this.state.studio_id || (this.state.studio_id === user.studio_id))) {
                        data.labels.push(user.initials)
                        default_dataset.push(0)
                    }
                })

                const palette = this.extended_palette
                Object.keys(report_dataset).forEach((initials, index) => {
                    const user = this.raw_user_records.find(user => user.initials === initials)
                    if(user && (!this.state.filter || (this.state.filter === user.role)) && (!this.state.studio_id || (this.state.studio_id === user.studio_id))) {
                        Object.keys(report_dataset[initials]).forEach(project_id => {
                            if(!project_datasets[project_id]) {
                                const project = this.entity_record("project", parseInt(project_id)) || {}
                                if(!this.project_colors[project_id]) {
                                    if(project.hex_color) this.project_colors[project_id] = project.hex_color
                                    else {
                                        this.project_colors[project_id] = palette[this.palette_index % palette.length]
                                        this.palette_index++
                                    }
                                }
                                project_datasets[project_id] = {
                                    label: this.nibnut_filter("nibnut.smart-truncate", [project.number + (parseInt(project.number_suffix) ? `.${project.number_suffix}` : "") + " " + project.name, 30]),
                                    backgroundColor: project.hex_color,
                                    borderColor: "#ffffff",
                                    borderWidth: 1,
                                    categoryPercentage: 0.9,
                                    barPercentage: 0.8,
                                    data: default_dataset.slice(),
                                    project: project
                                }
                            }

                            project_datasets[project_id].data[index] = report_dataset[initials][project_id]
                        })
                    }
                })
                data.datasets = Object.values(project_datasets)

                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.ymax = 0
                for(let loop = 0; loop < data.labels.length; loop++) {
                    let period_total = 0
                    data.datasets.forEach(dataset => {
                        period_total += parseFloat(dataset.data[loop]) || 0
                    })
                    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                    this.ymax = Math.max(period_total, this.ymax)
                }
            }

            return data
        },
        rows () {
            let rows = []
            if(this.report) {
                const range_start = this.$moment().startOf("isoWeek")
                switch (this.state.scope) {
                case "next-week":
                    range_start.add(1, "weeks")
                    break
                case "two-weeks-out":
                    range_start.add(2, "weeks")
                    break
                case "three-weeks-out":
                    range_start.add(3, "weeks")
                    break
                case "four-weeks-out":
                    range_start.add(4, "weeks")
                    break
                case "last-week":
                    range_start.subtract(1, "weeks")
                    break
                case "two-weeks-ago":
                    range_start.subtract(2, "weeks")
                    break
                case "three-weeks-ago":
                    range_start.subtract(3, "weeks")
                    break
                case "four-weeks-ago":
                    range_start.subtract(4, "weeks")
                    break
                case "five-weeks-ago":
                    range_start.subtract(5, "weeks")
                    break
                }
                const range_end = this.$moment(range_start).endOf("isoWeek")

                const report_dataset = this.report.dataset[range_start.format("YYYY-MM-DD")] || []
                let project_ids = []
                Object.keys(report_dataset).forEach(initials => {
                    project_ids = project_ids.concat(Object.keys(report_dataset[initials]))
                })

                rows = this.entity_records("task").filter(task => {
                    // if(!!task.milestone_id) return false
                    if(!this.$moment(this.ends_at(task)).isBetween(range_start, range_end, undefined, "[]")) return false
                    return (this.task_ids.indexOf(task.id) >= 0) && ((project_ids.indexOf(task.project_id) >= 0) || (project_ids.indexOf(`${task.project_id}`) >= 0))
                })
            }

            if(this.state.total !== rows.length) {
                this.set_state_values({
                    total: rows.length,
                    found: rows.length
                })
            }

            return rows
        }
    },
    data () {
        return {
            report_id: "milestones",

            default_state: {
                scope: "near-future",
                mode: "absolute",
                filter: "",
                user_id: 0,
                studio_id: 0,
                version_id: 0,
                per_page: 0,
                page: 1,
                sort_by: "due_at",
                sort_dir: "asc",
                total: 0,
                found: 0
            },
            columns: {
                ends_at_as_string: { label: "Due Date", sort: null, type: "plain" },
                sortable_number: { label: "Project # / Name", sort: null, type: "plain" },
                phase_name: { label: "Phase", sort: false, type: "plain" },
                title: { label: "Deadline", sort: false, type: "plain" },
                studio_list: { label: "Office(s)", sort: false, type: "plain", cell_class: "lam-col-fee" },
                team_list: { label: "Team", sort: false, type: "plain" }
            },

            palette_index: 0,
            project_colors: {},
            task_ids: []
        }
    }
}
</script>
